// Today's strength work, grouped by exercise. Tapping a group deletes it
// (with a confirm) — the same forgiving pattern as the meal rows.
import { Dumbbell, Plus } from "lucide-react";
import { useT } from "../lib/i18n";
import { useConfirm } from "../lib/confirm";
import { useToast } from "../lib/toast";
import { api } from "../lib/api";
import { hapticWarning } from "../lib/haptics";
import { titleCase } from "./WorkoutSheet";
import type { WorkoutSet } from "@shared/types";

interface Props {
  date: string;
  sets: WorkoutSet[];
  onOpen: () => void;
  onChanged: () => Promise<void> | void;
}

export function WorkoutCard({ date, sets, onOpen, onChanged }: Props) {
  const t = useT();
  const confirm = useConfirm();
  const toast = useToast();

  const byExercise = new Map<string, WorkoutSet[]>();
  for (const s of sets) {
    if (!byExercise.has(s.exercise)) byExercise.set(s.exercise, []);
    byExercise.get(s.exercise)!.push(s);
  }
  const volume = sets.reduce((v, s) => v + s.reps * s.weight_kg, 0);

  async function removeExercise(exercise: string) {
    if (!(await confirm({
      message: t("workout.confirm_delete", { name: titleCase(exercise) }),
      danger: true, confirmLabel: t("common.delete"),
    }))) return;
    try {
      await api.deleteWorkoutByExercise(date, exercise);
      hapticWarning();
      await onChanged();
    } catch {
      toast.error(t("common.failed"));
    }
  }

  return (
    <section className="glass rounded-2xl p-3.5">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="w-9 h-9 rounded-xl bg-surface-strong flex items-center justify-center">
          <Dumbbell size={18} />
        </span>
        <h3 className="text-sm font-semibold flex-1">{t("workout.title")}</h3>
        {sets.length > 0 && (
          <span className="text-sm text-fg-muted tabular-nums">
            {t("workout.volume", { kg: String(Math.round(volume)) })}
          </span>
        )}
        <button
          onClick={onOpen}
          aria-label={t("workout.log_title")}
          className="w-11 h-11 rounded-full bg-brand text-on-brand flex items-center justify-center flex-shrink-0 press"
        >
          <Plus size={20} aria-hidden="true" />
        </button>
      </div>

      {byExercise.size > 0 && (
        <div className="mt-3 space-y-2">
          {[...byExercise.entries()].map(([exercise, rows]) => (
            <button
              key={exercise}
              onClick={() => removeExercise(exercise)}
              className="w-full flex items-baseline gap-3 text-left press"
            >
              <span className="text-sm font-medium flex-1 truncate">{titleCase(exercise)}</span>
              <span className="text-sm text-fg-muted tabular-nums">
                {rows.map((r) => `${r.reps}×${r.weight_kg}`).join(" · ")}
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
